import { useCallback, useLayoutEffect, useRef } from 'react';
import { loadYouTubeIframeApi } from './loadYouTubeIframeApi';

const YT_PLAYING = 1;
const YT_PAUSED = 2;
const YT_ENDED = 0;

function destroyYouTubePlayer(playerRef, mountRef, container, clearProgressTimer) {
  clearProgressTimer();

  const player = playerRef.current;
  if (player?.destroy) {
    try {
      player.destroy();
    } catch {
      // Player may already be detached from the DOM.
    }
  }
  playerRef.current = null;

  const mountNode = mountRef.current;

  if (mountNode && container?.contains(mountNode)) {
    container.removeChild(mountNode);
  }

  mountRef.current = null;

  if (container) {
    container.replaceChildren();
  }
}

/**
 * Embeds a YouTube video via the IFrame API and wires it to VideoPlayer callbacks.
 * Uses an inner mount node so React can unmount the outer container safely.
 */
export function useYouTubeEmbed({
  videoId,
  resetKey,
  hasStarted,
  playing,
  muted = false,
  setPlaying,
  onDurationChange,
  onTimeUpdate,
  onEnded,
  startAtRef,
}) {
  const containerRef = useRef(null);
  const mountRef = useRef(null);
  const playerRef = useRef(null);
  const progressTimerRef = useRef(null);
  const destroyedRef = useRef(false);

  const setPlayingRef = useRef(setPlaying);
  const onDurationChangeRef = useRef(onDurationChange);
  const onTimeUpdateRef = useRef(onTimeUpdate);
  const onEndedRef = useRef(onEnded);
  const mutedRef = useRef(muted);

  setPlayingRef.current = setPlaying;
  onDurationChangeRef.current = onDurationChange;
  onTimeUpdateRef.current = onTimeUpdate;
  onEndedRef.current = onEnded;
  mutedRef.current = muted;

  const clearProgressTimer = useCallback(() => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  }, []);

  const startProgressTimer = useCallback((player, resumeAt = 0) => {
    clearProgressTimer();
    // Seek-to-resume is async. Ignore the leading 0 reports so the scrubber
    // does not jump back to the start, but only for a short settle window.
    const resumeGateUntil = resumeAt > 0.25 ? Date.now() + 2000 : 0;

    progressTimerRef.current = setInterval(() => {
      if (destroyedRef.current) {
        return;
      }

      const time = player?.getCurrentTime?.();
      if (!Number.isFinite(time)) {
        return;
      }

      if (resumeGateUntil && time + 0.75 < resumeAt && Date.now() < resumeGateUntil) {
        return;
      }

      onTimeUpdateRef.current?.(time);
    }, 250);
  }, [clearProgressTimer]);

  useLayoutEffect(() => {
    if (!videoId || !hasStarted || !containerRef.current) {
      return undefined;
    }

    let cancelled = false;
    destroyedRef.current = false;
    const container = containerRef.current;
    const mountNode = document.createElement('div');
    mountNode.className = 'ds-video-player__youtube-mount';
    container.appendChild(mountNode);
    mountRef.current = mountNode;

    loadYouTubeIframeApi()
      .then((YT) => {
        if (cancelled || !mountRef.current) {
          return;
        }

        const startAt = Math.max(0, Number(startAtRef?.current) || 0);

        playerRef.current = new YT.Player(mountRef.current, {
          videoId,
          playerVars: {
            autoplay: 1,
            controls: 0,
            disablekb: 1,
            fs: 0,
            modestbranding: 1,
            playsinline: 1,
            rel: 0,
            origin: window.location.origin,
            start: Math.floor(startAt),
          },
          events: {
            onReady: (event) => {
              const duration = event.target.getDuration();
              if (Number.isFinite(duration) && duration > 0) {
                onDurationChangeRef.current?.(duration);
              }

              if (mutedRef.current) {
                event.target.mute?.();
              } else {
                event.target.unMute?.();
              }

              // `start` is whole seconds; seek again so a mid-video resume is exact.
              if (startAt > 0.25) {
                event.target.seekTo?.(startAt, true);
                onTimeUpdateRef.current?.(startAt);
              }

              event.target.playVideo();
            },
            onStateChange: (event) => {
              // A player torn down on step change can still emit events; they
              // must not reach the next step's playback state.
              if (cancelled || destroyedRef.current) {
                return;
              }

              if (event.data === YT_PLAYING) {
                setPlayingRef.current?.(true);
                startProgressTimer(event.target, startAt);
                return;
              }

              if (event.data === YT_PAUSED) {
                setPlayingRef.current?.(false);
                clearProgressTimer();
                return;
              }

              if (event.data === YT_ENDED) {
                setPlayingRef.current?.(false);
                clearProgressTimer();
                onEndedRef.current?.();
              }
            },
          },
        });
      })
      .catch(() => {
        setPlayingRef.current?.(false);
      });

    return () => {
      cancelled = true;
      destroyedRef.current = true;
      destroyYouTubePlayer(playerRef, mountRef, container, clearProgressTimer);
    };
  }, [videoId, resetKey, hasStarted, clearProgressTimer, startProgressTimer]);

  useLayoutEffect(() => {
    const player = playerRef.current;
    if (!player?.getPlayerState) {
      return;
    }

    const state = player.getPlayerState();

    if (playing && state !== YT_PLAYING) {
      player.playVideo?.();
      return;
    }

    if (!playing && state === YT_PLAYING) {
      player.pauseVideo?.();
    }
  }, [playing]);

  useLayoutEffect(() => {
    const player = playerRef.current;
    if (!player) {
      return;
    }

    if (muted) {
      player.mute?.();
      return;
    }

    player.unMute?.();
  }, [muted]);

  const seekTo = useCallback((seconds) => {
    playerRef.current?.seekTo?.(seconds, true);
    onTimeUpdateRef.current?.(seconds);
  }, []);

  return {
    containerRef,
    seekTo,
  };
}
